import { InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';

import { Paginated } from 'models/Paginated';
import { ChatMessage, chatQueryKeys, sendChatMessage, SendMessageInput } from 'services/api/chat';
import { useToastActions } from 'stores/toastStore';

type MessagePages = InfiniteData<Paginated<ChatMessage>, string | undefined>;

// Splice the persisted message onto the newest (first) page, which is stored newest-first —
// so after MessageList's reverse it lands at the bottom. Seeds the cache when the channel
// had no messages yet (the empty-state → first-message transition). Idempotent by id.
function appendMessage(existing: MessagePages | undefined, message: ChatMessage): MessagePages {
  if (!existing || existing.pages.length === 0) {
    return {
      pages: [{ data: [message], meta: { totalRecords: 1, totalPages: 1 }, links: { next: null, prev: null } }],
      pageParams: [undefined],
    };
  }

  const [newest, ...older] = existing.pages;
  if (newest.data.some((m) => m.id === message.id)) return existing;

  return { ...existing, pages: [{ ...newest, data: [message, ...newest.data] }, ...older] };
}

/**
 * Send a message and append the response to the cache — write-response-as-truth, no
 * optimistic insert (the useWorkflow.ts precedent). The returned copy is the source of
 * truth the list renders, so it appears at the bottom immediately and survives refresh.
 */
export default function useSendMessage(conversationId: string) {
  const queryClient = useQueryClient();
  const { addToast } = useToastActions();

  const mutation = useMutation({
    mutationFn: (body: SendMessageInput) => sendChatMessage(conversationId, body),
    onSuccess: (message) => {
      queryClient.setQueryData<MessagePages>(chatQueryKeys.messages(conversationId), (existing) =>
        appendMessage(existing, message)
      );
    },
    onError: () => addToast({ text: 'Your message couldn’t be sent. Please try again.' }),
  });

  return { sendMessage: mutation.mutate, isSending: mutation.isPending };
}
